import { useState } from 'react'

import { LocationValue } from '@flowwai-frontend/shared/common-components'

import { isValidClosedAreaWithoutSelfIntersections } from '../components/MapBoxMarkers/utils'
import { MapCoordinates } from '../types'
import { useMapsUtilityContext } from './useMapsUtilityContext'

interface HookArgs {
   geoShape: LocationValue
   historyRef: React.MutableRefObject<LocationValue[]>
   onUpdateGeoShape: (geoShape: LocationValue) => void
   selectedMarkerIndex: number | null
   setSelectedMarkerIndex: (markerIndex: number | null) => void
}

export const useUpdateGeoShape = (args: HookArgs) => {
   const {
      geoShape,
      historyRef,
      onUpdateGeoShape,
      selectedMarkerIndex,
      setSelectedMarkerIndex
   } = args

   const [errorMessage, setErrorMessage] = useState<string | null>(null)

   const { mapsT } = useMapsUtilityContext()

   const onUpdateSingleCoordinate = (coordinates?: MapCoordinates) => {
      onUpdateGeoShape({ type: 'POINT', value: coordinates })
   }

   const onUpdateMultipleCoordinates = (coordinates: MapCoordinates[]) => {
      if (geoShape.type === 'LINE') {
         const isValid = coordinates.length >= 2 || coordinates.length === 0
         setErrorMessage(isValid ? null : mapsT('needAtLeastTwoPoints'))
      }

      onUpdateGeoShape({
         type: geoShape.type,
         value: coordinates
      } as LocationValue)
   }

   const onUpdatePolygonCoordinates = (coordinates: MapCoordinates[]) => {
      if (geoShape.type !== 'POLYGON') return

      const hasValidNumberOfPoints = coordinates.length > 3

      const hasEmptyResponse = coordinates.length === 0

      if (hasValidNumberOfPoints) {
         const isValid = isValidClosedAreaWithoutSelfIntersections(coordinates)
         setErrorMessage(isValid ? null : mapsT('areaIntersectionError'))
      } else if (!hasEmptyResponse) {
         setErrorMessage(mapsT('needAtLeastThreePoints'))
      } else if (hasEmptyResponse) {
         setErrorMessage(null)
      }

      onUpdateGeoShape({
         type: geoShape.type,
         value: coordinates
      })
   }

   const validateAndUpdateGeoShape = (geoShape: LocationValue) => {
      switch (geoShape.type) {
         case 'POINT': {
            onUpdateSingleCoordinate(geoShape.value)
            break
         }
         case 'POLYGON': {
            onUpdatePolygonCoordinates(geoShape.value ?? [])
            break
         }
         default: {
            onUpdateMultipleCoordinates(geoShape.value ?? [])
         }
      }
   }

   const onAddCoordinates = (clickedCoordinates: MapCoordinates): void => {
      switch (geoShape.type) {
         case 'POLYGON': {
            const newCoordinates = [...(geoShape.value ?? [])]
            if (newCoordinates.length) {
               newCoordinates.splice(
                  newCoordinates.length - 1,
                  0,
                  clickedCoordinates
               )
            } else {
               newCoordinates.push(clickedCoordinates, clickedCoordinates)
            }

            validateAndUpdateGeoShape({
               type: 'POLYGON',
               value: newCoordinates
            })
            break
         }
         case 'LINE': {
            const updatedCoordinates = [
               ...(geoShape.value ?? []),
               clickedCoordinates
            ]

            validateAndUpdateGeoShape({
               type: 'LINE',
               value: updatedCoordinates
            })
            break
         }
         case 'MULTI_POINT': {
            validateAndUpdateGeoShape({
               type: 'MULTI_POINT',
               value: [...(geoShape.value ?? []), clickedCoordinates]
            })
            break
         }
         case 'POINT': {
            validateAndUpdateGeoShape({
               type: 'POINT',
               value: clickedCoordinates
            })
            break
         }
      }
   }

   const onDeleteCoordinates = (index: number): void => {
      switch (geoShape.type) {
         case 'POINT': {
            validateAndUpdateGeoShape({
               type: 'POINT'
            })
            break
         }
         default: {
            const updatedCoordinates = [...(geoShape.value ?? [])]
            const isFirstPolygonMarker =
               index === 0 && geoShape.type === 'POLYGON'

            if (isFirstPolygonMarker) {
               updatedCoordinates.shift()
               updatedCoordinates.pop()

               const newFirstCoordinates = updatedCoordinates.at(0)
               if (newFirstCoordinates)
                  updatedCoordinates.push(newFirstCoordinates)
            } else {
               updatedCoordinates.splice(index, 1)
            }

            validateAndUpdateGeoShape({
               type: geoShape.type,
               value: updatedCoordinates
            })
         }
      }

      const isActiveMarker = selectedMarkerIndex === index
      if (isActiveMarker) setSelectedMarkerIndex(null)
   }

   const onMoveMarker = (coordinates: MapCoordinates, index: number): void => {
      switch (geoShape.type) {
         case 'POINT': {
            validateAndUpdateGeoShape({
               type: 'POINT',
               value: coordinates
            })
            break
         }
         default: {
            const updatedCoordinates = [...(geoShape.value ?? [])]
            const isFirstPolygonMarker =
               index === 0 && geoShape.type === 'POLYGON'

            if (isFirstPolygonMarker) {
               updatedCoordinates.splice(0, 1, coordinates)
               updatedCoordinates.splice(
                  updatedCoordinates.length - 1,
                  1,
                  coordinates
               )
            } else {
               updatedCoordinates.splice(index, 1, coordinates)
            }

            validateAndUpdateGeoShape({
               type: geoShape.type,
               value: updatedCoordinates
            })
         }
      }
   }

   const onUndo = (): void => {
      const hasPreviousItem = historyRef.current.length > 1
      if (!hasPreviousItem) return

      const updatedHistory = [...historyRef.current]
      updatedHistory.pop()

      const lastItem = updatedHistory.at(-1)
      updatedHistory.pop()
      historyRef.current = updatedHistory

      if (lastItem) validateAndUpdateGeoShape(lastItem)
   }

   return {
      errorMessage,
      validateAndUpdateGeoShape,
      onAddCoordinates,
      onDeleteCoordinates,
      onMoveMarker,
      onUndo
   }
}
